import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AnswerEditor } from '../components/AnswerEditor';
import { SheetBudgetItem } from '../components/InkPad';
import { McqEditor } from '../components/McqEditor';
import { useExamSession, SaveStatus } from '../hooks/useExamSession';
import RichTextView from '../components/RichTextView';
import { htmlToPlainText } from '../utils/richtext';
import { DEFAULT_SHEET_CONFIG } from '../utils/answers';
import type { Question, SectionInfo, SheetType } from '../types/models';

// Extra sheets a student can pull in while writing, in toolbar order. The exam's main sheet is
// dropped from this list (it's added via the plain "Add page" button). All are uncapped — staff
// only fix the main ruled/unruled style; students choose any of these as they need them.
const EXTRA_SHEET_ORDER: SheetType[] = ['graph', 'engineering', 'log', 'plain', 'ruled'];

function fmt(sec: number) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

function SaveBadge({ status }: { status: SaveStatus | undefined }) {
  if (!status || status === 'idle') return <span className="save-indicator">Ready</span>;
  const label = status === 'saving' ? 'Saving…'
    : status === 'saved' ? 'Saved'
      : 'Retrying…';
  return <span className={`save-indicator ${status}`}>{label}</span>;
}

interface NavProps {
  sections: SectionInfo[];
  questions: Question[];
  answered: Set<string>;
  currentIndex: number;
  onPick: (i: number) => void;
  open: boolean;
  onClose: () => void;
}

interface NavGroup {
  id: string;
  title: string;
  section: SectionInfo | null;
  indices: number[];
}

const UNSECTIONED_ID = '__unsectioned__';

// ---- Either/or helpers: a choice group's alternatives count as one navigable unit ----
type ChoiceQ = { id: string; sectionId: string | null; choiceGroupId: string | null; choiceOrder: number };

/** The lead alternative (A) is the navigable representative of a choice group. */
const isLeadQ = (q: { choiceGroupId: string | null; choiceOrder: number }) =>
  !q.choiceGroupId || q.choiceOrder === 0;

/** All alternatives of q's choice group (sorted A,B), or just [q] when standalone. */
function groupMembers<T extends ChoiceQ>(questions: T[], q: T): T[] {
  return q.choiceGroupId
    ? questions.filter(x => x.choiceGroupId === q.choiceGroupId).slice().sort((a, b) => a.choiceOrder - b.choiceOrder)
    : [q];
}

/** A choice group counts as answered when ANY of its alternatives has an answer. */
const groupAnswered = (questions: ChoiceQ[], q: ChoiceQ, answered: Set<string>) =>
  groupMembers(questions, q).some(m => answered.has(m.id));

function buildNavGroups(sections: SectionInfo[], questions: Question[]): NavGroup[] {
  // Only lead alternatives are navigable; the B alternative renders alongside A.
  const groups: NavGroup[] = sections.map(s => ({
    id: s.id,
    title: s.title,
    section: s,
    indices: questions.map((q, i) => ({ q, i })).filter(x => x.q.sectionId === s.id && isLeadQ(x.q)).map(x => x.i),
  }));
  const unsectioned = questions.map((q, i) => ({ q, i })).filter(x => !x.q.sectionId && isLeadQ(x.q)).map(x => x.i);
  if (unsectioned.length > 0) {
    groups.push({ id: UNSECTIONED_ID, title: 'Other', section: null, indices: unsectioned });
  }
  return groups;
}

function ExamSideMenu({ sections, questions, answered, currentIndex, onPick, open, onClose }: NavProps) {
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  const panelRef = useRef<HTMLElement | null>(null);

  const groups = useMemo(() => buildNavGroups(sections, questions), [sections, questions]);

  // Display number per navigable unit (lead alternatives only), counted across the whole paper.
  const leadOrder = useMemo(() => {
    const m = new Map<number, number>();
    let n = 0;
    questions.forEach((qq, i) => { if (isLeadQ(qq)) { n++; m.set(i, n); } });
    return m;
  }, [questions]);

  // Close on Escape so a student can re-focus the canvas without reaching for the mouse.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Keep the panel mounted (so section-collapse state survives toggles) but
  // remove it from the tab order while closed.
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    if (open) el.removeAttribute('inert');
    else el.setAttribute('inert', '');
  }, [open]);

  function pick(i: number) {
    onPick(i);
    onClose();
  }

  return (
    <>
      <div
        className={`side-backdrop${open ? ' open' : ''}`}
        onClick={onClose}
        aria-hidden
      />
      <aside
        ref={panelRef}
        id="exam-navigator"
        className={`exam-side overlay${open ? ' open' : ''}`}
        aria-label="Exam navigation"
        aria-hidden={!open}
      >
        <div className="side-header">
          <span>Navigator</span>
          <button
            className="side-toggle"
            aria-label="Close navigation"
            title="Close"
            onClick={onClose}
          >×</button>
        </div>

        {groups.map(g => {
          const isClosed = !!closed[g.id];
          return (
            <div key={g.id} className="side-section">
              <button
                className="side-section-head"
                aria-expanded={!isClosed}
                onClick={() => setClosed(c => ({ ...c, [g.id]: !c[g.id] }))}
              >
                <span className={`chevron${isClosed ? ' closed' : ''}`} aria-hidden>▾</span>
                <span className="title">{g.title}</span>
                <span className="muted side-section-meta">
                  {g.section
                    ? sectionProgressLabel(g.section, questions, answered)
                    : `${g.indices.filter(i => groupAnswered(questions, questions[i], answered)).length} / ${g.indices.length}`}
                </span>
              </button>
              {!isClosed && (
                <ul className="side-question-list" role="list">
                  {g.indices.map((i, displayNum) => {
                    const item = questions[i];
                    const members = groupMembers(questions, item);
                    const isChoice = members.length > 1;
                    // Active when the current question is any member of this unit's group.
                    const isActive = members.some(m => m.id === questions[currentIndex]?.id);
                    const isAnswered = groupAnswered(questions, item, answered);
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          className={`side-question-item${isActive ? ' active' : ''}${isAnswered ? ' answered' : ''}`}
                          onClick={() => pick(i)}
                          aria-current={isActive ? 'true' : undefined}
                          title={htmlToPlainText(item.prompt)}
                        >
                          <span className="side-question-num" aria-hidden>{leadOrder.get(i) ?? displayNum + 1}</span>
                          <span className="side-question-text">
                            {isChoice && <strong style={{ color: 'var(--accent)' }}>Either/Or: </strong>}
                            {htmlToPlainText(item.prompt) || <em className="muted">(no prompt)</em>}
                          </span>
                          {isAnswered && (
                            <span className="side-question-mark" aria-label="answered" title="Answered">✓</span>
                          )}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </aside>
    </>
  );
}

function sectionProgressLabel(
  section: SectionInfo,
  questions: ChoiceQ[],
  answered: Set<string>
) {
  // Count navigable units: each standalone question and each either/or pair counts once.
  const units = questions.filter(q => q.sectionId === section.id && isLeadQ(q));
  const total = units.length;
  const done = units.filter(u => groupAnswered(questions, u, answered)).length;
  if (section.requiredAnswers > 0) {
    const need = Math.min(section.requiredAnswers, total);
    const remaining = Math.max(0, need - done);
    return `Answer ${need} of ${total} · ${done} done${remaining > 0 ? `, ${remaining} to go` : ' ✓'}`;
  }
  return `Answer all ${total} · ${done} done`;
}

export function ExamRuntimePage() {
  const { examId } = useParams();
  const nav = useNavigate();
  const [started, setStarted] = useState(false);
  const s = useExamSession(examId!, started);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // In-app submit confirmation. We avoid window.confirm() because native dialogs drop the
  // browser out of fullscreen and trip the lockdown warning.
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(() => !!document.fullscreenElement);
  const [violations, setViolations] = useState(0);
  // Exams are written in portrait only. We try to hard-lock the device to portrait once in
  // fullscreen; on devices that ignore the lock API (iPadOS/Safari) this flag drives a
  // "rotate to portrait" overlay that blocks the exam while the tablet is held in landscape.
  const [isLandscape, setIsLandscape] = useState<boolean>(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(orientation: landscape)')?.matches);

  const firedCheckpointsRef = useRef<Set<number>>(new Set());
  const toastTimerRef = useRef<number | undefined>(undefined);
  const lastViolationRef = useRef<number>(0);

  // Publish the sticky exam topbar's real height as a CSS var so the pinned question sits exactly
  // beneath it on any device. A callback ref measures on mount; a resize listener keeps it current.
  const topbarElRef = useRef<HTMLElement | null>(null);
  // Use the topbar's visual BOTTOM edge (not offsetHeight) so the sticky question clears any
  // OS status-bar / safe-area offset on the device (the topbar is sticky at top:0). Measured
  // after paint and re-measured on start/resize so the value is never 0 (which would dock the
  // question behind the topbar and make it look like it vanished on scroll).
  const measureTopbar = useCallback(() => {
    const el = topbarElRef.current;
    if (!el) return;
    const bottom = Math.round(el.getBoundingClientRect().bottom);
    if (bottom > 0) document.documentElement.style.setProperty('--exam-topbar-h', `${bottom}px`);
  }, []);
  const setTopbarRef = useCallback((el: HTMLElement | null) => {
    topbarElRef.current = el;
    if (el) requestAnimationFrame(measureTopbar);
  }, [measureTopbar]);
  useEffect(() => {
    const id = requestAnimationFrame(measureTopbar);
    window.addEventListener('resize', measureTopbar);
    return () => { cancelAnimationFrame(id); window.removeEventListener('resize', measureTopbar); };
  }, [measureTopbar, started]);

  // Show a transient banner; auto-dismisses after a few seconds.
  const showToast = useCallback((msg: string) => {
    setToast(msg);
    window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setToast(null), 6000);
  }, []);

  // Record a lockdown breach (tab switch / fullscreen exit). De-duped so a single action
  // that fires both `blur` and `visibilitychange` only counts once.
  const registerViolation = useCallback((reason: string) => {
    const now = Date.now();
    if (now - lastViolationRef.current < 1000) return;
    lastViolationRef.current = now;
    setViolations(v => v + 1);
    showToast(`⚠ ${reason}. This is recorded.`);
  }, [showToast]);

  // Ask the device to lock to portrait. Only works while in fullscreen and on Chromium/Android;
  // Safari/iPadOS reject it (the rotate overlay covers those). Cast through any — the lock method
  // isn't in the DOM typings.
  const lockPortrait = useCallback(() => {
    try {
      const o = screen.orientation as any;
      o?.lock?.('portrait')?.catch?.(() => { /* unsupported — overlay enforces it */ });
    } catch { /* ScreenOrientation.lock not available */ }
  }, []);

  const enterFullscreen = useCallback(() => {
    const req = document.documentElement.requestFullscreen?.();
    if (req && typeof req.then === 'function') {
      req.then(() => lockPortrait()).catch(() => { /* user can retry */ });
    } else {
      lockPortrait();
    }
  }, [lockPortrait]);

  // Begin the exam: enter fullscreen (this click is the required user gesture) and start the
  // attempt/timer. Until this runs the student only sees the instructions & rules.
  const beginExam = useCallback(() => {
    enterFullscreen();
    setStarted(true);
  }, [enterFullscreen]);

  // Hook order MUST be stable across renders, so memoise before any early return.
  const sectionsById = useMemo(() => {
    const m = new Map<string, SectionInfo>();
    s.paper?.sections.forEach(sec => m.set(sec.id, sec));
    return m;
  }, [s.paper]);

  // Staff fix only the main sheet style; students may add any of the other sheet types freely.
  const sheetConfig = s.paper?.answerSheets ?? DEFAULT_SHEET_CONFIG;
  const sheetBudget: SheetBudgetItem[] = useMemo(
    () => EXTRA_SHEET_ORDER.filter(t => t !== sheetConfig.mainSheet).map(type => ({ type })),
    [sheetConfig.mainSheet]);

  // --- Lock the student into the exam: warn on tab close/refresh and block the back button
  // so they can't navigate to other pages once the exam has started (until they submit). ---
  useEffect(() => {
    if (!started || submitted) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.history.pushState(null, '', window.location.href);
    const onPop = () => {
      window.history.pushState(null, '', window.location.href);
      showToast('You can’t leave the exam until you submit or the time runs out.');
    };
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('popstate', onPop);
    };
  }, [started, submitted, showToast]);

  // --- Remaining-time alerts: split the exam duration into 5 parts and warn the student as
  // each quarter-point passes (at 80%, 60%, 40%, 20% of the duration). ---
  useEffect(() => {
    if (!s.paper || !s.attempt || s.remaining <= 0) return;
    const total = s.paper.durationMinutes * 60;
    const checkpoints = [0.8, 0.6, 0.4, 0.2].map(f => Math.round(total * f)).filter(c => c > 0);
    for (const cp of checkpoints) {
      if (s.remaining <= cp && !firedCheckpointsRef.current.has(cp)) {
        firedCheckpointsRef.current.add(cp);
        const mins = Math.max(1, Math.round(s.remaining / 60));
        showToast(`⏰ ${mins} minute${mins === 1 ? '' : 's'} remaining`);
      }
    }
  }, [s.remaining, s.paper, s.attempt, showToast]);

  // --- Auto-submit when the timer reaches zero. ---
  useEffect(() => {
    if (!s.attempt || s.remaining > 0 || submitted || submitting) return;
    (async () => {
      setSubmitting(true);
      try {
        await s.submit(true);
        setSubmitted(true);
      } finally {
        setSubmitting(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s.remaining, s.attempt, submitted, submitting]);

  // --- Fullscreen lockdown: track fullscreen state and count any exit as a breach. ---
  useEffect(() => {
    if (!started || submitted) return;
    const onFsChange = () => {
      const fs = !!document.fullscreenElement;
      setIsFullscreen(fs);
      if (!fs) registerViolation('You exited fullscreen');
    };
    document.addEventListener('fullscreenchange', onFsChange);
    setIsFullscreen(!!document.fullscreenElement);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, [started, submitted, registerViolation]);

  // --- Tab/window-switch detection: warn and count when the student leaves the exam.
  // Only active while in fullscreen so the initial "enter fullscreen" click (which blurs the
  // window) doesn't register a false breach. ---
  useEffect(() => {
    if (submitted || !isFullscreen) return;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') registerViolation('You switched away from the exam');
    };
    const onBlur = () => registerViolation('You switched away from the exam');
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
    };
  }, [submitted, isFullscreen, registerViolation]);

  // --- Pin the viewport while the exam is in progress: prevent body scroll / overscroll so a
  // tablet's downward swipe can't reveal the browser chrome (address bar / navigation) that a
  // student could use to slip back to the home page. Scroll stays inside the exam content. ---
  useEffect(() => {
    if (!started || submitted) return;
    document.body.classList.add('exam-locked');
    return () => document.body.classList.remove('exam-locked');
  }, [started, submitted]);

  // --- Portrait-only: track orientation and re-assert the portrait lock. The overlay (below)
  // blocks writing whenever the tablet is landscape, so even devices that ignore the lock API
  // can't be used sideways during the exam. ---
  useEffect(() => {
    if (!started || submitted) return;
    const mq = window.matchMedia('(orientation: landscape)');
    const update = () => {
      setIsLandscape(mq.matches);
      if (mq.matches) lockPortrait(); // nudge it back to portrait if the device rotated
    };
    update();
    mq.addEventListener?.('change', update);
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      mq.removeEventListener?.('change', update);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, [started, submitted, lockPortrait]);

  // --- Block copy / cut / paste / right-click while the exam is open. ---
  useEffect(() => {
    if (!started || submitted) return;
    const prevent = (e: Event) => e.preventDefault();
    document.addEventListener('contextmenu', prevent);
    document.addEventListener('copy', prevent);
    document.addEventListener('cut', prevent);
    document.addEventListener('paste', prevent);
    return () => {
      document.removeEventListener('contextmenu', prevent);
      document.removeEventListener('copy', prevent);
      document.removeEventListener('cut', prevent);
      document.removeEventListener('paste', prevent);
    };
  }, [started, submitted]);

  // --- Leave fullscreen once the exam is over. ---
  useEffect(() => {
    if (submitted && document.fullscreenElement) document.exitFullscreen?.().catch(() => { });
  }, [submitted]);

  if (s.loadError) return <div className="container"><div className="card error">{s.loadError}</div></div>;
  if (!s.paper) return <div className="container"><div className="card muted">Loading exam…</div></div>;

  // --- Instructions & rules screen (shown before the exam — and timer — starts). ---
  if (!started) {
    const p = s.paper;
    return (
      <div className="container">
        <div className="card" style={{ maxWidth: 720, margin: '32px auto' }}>
          <h1 style={{ marginTop: 0 }}>{p.title}</h1>
          {(p.subjectName || p.subjectCode) && (
            <div style={{ fontWeight: 600, marginBottom: 4 }}>
              {[p.subjectName, p.subjectCode].filter(Boolean).join(' · ')}
            </div>
          )}
          <div className="muted" style={{ marginBottom: 16 }}>
            {p.questions.length} questions · {p.totalMarks} marks · {p.durationMinutes} min
          </div>

          {p.instructions && (
            <div style={{ marginBottom: 16 }}>
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Instructions</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{p.instructions}</div>
            </div>
          )}

          <div className="card" style={{ background: 'var(--surface-2)', borderLeft: '4px solid var(--warn, #d97706)' }}>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>⚠ Before you start — please read</div>
            <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.7 }}>
              <li>The exam opens in <strong>fullscreen</strong>. Do not exit fullscreen until you submit.</li>
              <li>Do <strong>not switch tabs or apps</strong> — every switch is detected, counted and recorded.</li>
              <li>The <strong>timer starts when you click Start</strong> and does not pause, even if you leave.</li>
              <li>When the time is over, your exam is <strong>submitted automatically</strong>.</li>
              <li>You cannot return to other pages while the exam is in progress.</li>
              <li>Copy, paste and right‑click are disabled during the exam.</li>
            </ul>
          </div>

          <div className="row" style={{ justifyContent: 'flex-end', marginTop: 20, gap: 8 }}>
            <button onClick={() => nav('/exams')}>Cancel</button>
            <button className="primary" onClick={beginExam}>Start exam in fullscreen</button>
          </div>
        </div>
      </div>
    );
  }

  if (!s.attempt) return <div className="container"><div className="card muted">Starting exam…</div></div>;

  const questions = s.paper.questions;
  const q = questions[currentIndex];
  const timerClass = s.remaining < 60 ? 'danger' : s.remaining < 300 ? 'warn' : '';
  const currentSection = q.sectionId ? sectionsById.get(q.sectionId) : null;

  // Either/or: render the whole choice group on one page; navigation steps over lead units only.
  const currentGroup = groupMembers(questions, q);
  const isChoice = currentGroup.length > 1;
  const leadIndices = questions.map((_, i) => i).filter(i => isLeadQ(questions[i]));
  const currentLeadIndex = isLeadQ(q)
    ? currentIndex
    : questions.findIndex(x => x.choiceGroupId === q.choiceGroupId && x.choiceOrder === 0);
  const leadPos = leadIndices.indexOf(currentLeadIndex);
  const totalUnits = leadIndices.length;
  const answeredUnits = leadIndices.filter(i => groupAnswered(questions, questions[i], s.answered)).length;

  // --- "Answer N of M" cap: in a section that only requires some of its questions, once the
  // student has answered the required count they can't answer any more (the extras lock). ---
  const sectionUnitIdx = currentSection
    ? leadIndices.filter(i => questions[i].sectionId === currentSection.id)
    : [];
  const sectionNeed = currentSection && currentSection.requiredAnswers > 0
    ? Math.min(currentSection.requiredAnswers, sectionUnitIdx.length)
    : 0;
  const sectionAnswered = sectionUnitIdx.filter(i => groupAnswered(questions, questions[i], s.answered)).length;
  const currentUnitAnswered = groupAnswered(questions, q, s.answered);
  // Lock this question when the section's quota is already met and this one isn't part of it.
  const answerLocked = sectionNeed > 0 && sectionAnswered >= sectionNeed && !currentUnitAnswered;

  // --- Submit-time rules (manual submit only) --------------------------------------------
  // 1. Either/or: at most one of (a)/(b) may be answered.
  // 2. "Answer N of M": a capped section must not have more than N answered.
  const submitProblems: string[] = [];
  {
    const allLeads = questions.filter(isLeadQ);
    const bothAnswered = allLeads.filter(lead => {
      const members = groupMembers(questions, lead);
      return members.length > 1 && members.filter(m => s.answered.has(m.id)).length > 1;
    }).length;
    if (bothAnswered > 0) {
      submitProblems.push(bothAnswered === 1
        ? 'One either/or question has both options answered — keep only one (a) or (b).'
        : `${bothAnswered} either/or questions have both options answered — keep only one (a) or (b) for each.`);
    }
    for (const sec of s.paper.sections) {
      if (sec.requiredAnswers <= 0) continue;
      const secLeads = allLeads.filter(l => l.sectionId === sec.id);
      const need = Math.min(sec.requiredAnswers, secLeads.length);
      const ans = secLeads.filter(l => groupAnswered(questions, l, s.answered)).length;
      if (ans > need)
        submitProblems.push(`Section “${sec.title}”: answer at most ${need} question${need === 1 ? '' : 's'} — you have answered ${ans}.`);
    }
  }

  async function handleSubmit(auto = false) {
    if (submitting || submitted) return;
    // Block a manual submit that breaks the answer rules; auto-submit (time-up) always proceeds.
    if (!auto && submitProblems.length > 0) {
      showToast(submitProblems[0]);
      return;
    }
    setConfirmSubmitOpen(false);
    setSubmitting(true);
    try {
      await s.submit(auto);
      setSubmitted(true);
    } catch (e: any) {
      // Backend safety-net rejection (e.g. rules) — surface it and keep the exam open.
      showToast(e?.message ?? 'Could not submit. Please review your answers and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="container">
        <div className="card">
          <h2 style={{ marginTop: 0 }}>Submitted</h2>
          <div className="success" style={{ marginBottom: 16 }}>Your exam was submitted successfully.</div>
          <button className="primary" onClick={() => nav('/exams')}>Back to exam list</button>
        </div>
      </div>
    );
  }

  return (
    <>
      {!isFullscreen && (
        <div
          role="alertdialog"
          aria-label="Fullscreen required"
          style={{
            position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.88)',
            color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 16, textAlign: 'center', padding: 24
          }}
        >
          <h2 style={{ margin: 0 }}>Fullscreen required</h2>
          <p style={{ maxWidth: 460, opacity: 0.85 }}>
            This exam must be taken in fullscreen. Leaving fullscreen or switching tabs is
            recorded. Your timer keeps running.
          </p>
          {violations > 0 && (
            <p style={{ color: '#fca5a5', fontWeight: 600 }}>Warnings so far: {violations}</p>
          )}
          <button className="primary" onClick={enterFullscreen}>Enter fullscreen &amp; continue</button>
        </div>
      )}
      {isLandscape && (
        <div
          role="alertdialog"
          aria-label="Rotate to portrait"
          style={{
            position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.92)',
            color: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 16, textAlign: 'center', padding: 24
          }}
        >
          <div style={{ fontSize: 52, lineHeight: 1 }} aria-hidden>↻📱</div>
          <h2 style={{ margin: 0 }}>Please rotate to portrait</h2>
          <p style={{ maxWidth: 440, opacity: 0.85 }}>
            This exam must be taken in <strong>portrait</strong> orientation. Turn your tablet
            upright to continue — your timer keeps running.
          </p>
        </div>
      )}
      {toast && (
        <div
          role="alert"
          style={{
            position: 'fixed', top: 70, left: '50%', transform: 'translateX(-50%)',
            zIndex: 1000, background: 'var(--surface)', color: 'var(--text)',
            border: '1px solid var(--border)', borderLeft: '4px solid var(--warn, #d97706)',
            boxShadow: '0 6px 24px rgba(0,0,0,0.18)', borderRadius: 8,
            padding: '12px 18px', fontWeight: 600, maxWidth: '90vw'
          }}
        >
          {toast}
        </div>
      )}
      {confirmSubmitOpen && (
        <div className="rte-modal-backdrop" onMouseDown={() => setConfirmSubmitOpen(false)}>
          <div className="rte-modal" style={{ width: 'min(440px, 92vw)' }} onMouseDown={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Submit exam?</h3>
            {submitProblems.length > 0 ? (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, color: 'var(--danger)', marginBottom: 6 }}>
                  Please fix the following before submitting:
                </div>
                <ul style={{ margin: 0, paddingLeft: 20, color: 'var(--danger)' }}>
                  {submitProblems.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            ) : (
              <div style={{ marginBottom: 16 }}>
                You answered {answeredUnits} of {totalUnits} question{totalUnits === 1 ? '' : 's'}.
                You can't change answers after submitting.
              </div>
            )}
            <div className="rte-modal-actions">
              <button type="button" onClick={() => setConfirmSubmitOpen(false)} disabled={submitting}>
                {submitProblems.length > 0 ? 'Go back & fix' : 'Cancel'}
              </button>
              <button type="button" className="primary" onClick={() => handleSubmit(false)}
                      disabled={submitting || submitProblems.length > 0}>
                {submitting ? 'Submitting…' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="exam-shell">
      <header className="topbar" ref={setTopbarRef}>
        <div className="row" style={{ gap: 12 }}>
          <button
            className="topbar-btn"
            aria-label="Open exam navigation"
            aria-expanded={menuOpen}
            aria-controls="exam-navigator"
            onClick={() => setMenuOpen(o => !o)}
          >
            <span aria-hidden>☰</span>
            <span className="topbar-btn-label">Sections</span>
          </button>
          <div className="brand">{s.paper.title}</div>
        </div>
        <div className="row" style={{ gap: 16 }}>
          {violations > 0 && (
            <span title="Lockdown warnings (tab switches / fullscreen exits)"
                  style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger, #dc2626)' }}>
              ⚠ {violations}
            </span>
          )}
          <span style={{ fontSize: 13 }}>{answeredUnits} / {totalUnits} answered</span>
          <div className={`timer ${timerClass}`}>{fmt(s.remaining)}</div>
          <button className="primary" onClick={() => setConfirmSubmitOpen(true)} disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit'}
          </button>
        </div>
      </header>

      <ExamSideMenu
        sections={s.paper.sections}
        questions={questions}
        answered={s.answered}
        currentIndex={currentIndex}
        onPick={setCurrentIndex}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
      />

      <div className="container exam-attempt">
        <div className="exam-main">
          {s.paper.instructions && (
            <div className="card">
              <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>Instructions</div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{s.paper.instructions}</div>
            </div>
          )}
          <div className="card">
            {currentSection && (
              <div className="card" style={{ background: 'var(--surface-2)', marginBottom: 12, padding: 12 }}>
                <div style={{ fontWeight: 600 }}>{currentSection.title}</div>
                {currentSection.instructions && (
                  <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{currentSection.instructions}</div>
                )}
                <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                  {sectionProgressLabel(currentSection, questions, s.answered)}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <div>
                <div style={{ fontWeight: 600 }}>
                  Question {leadPos + 1} of {totalUnits} · {currentGroup[0].marks} mark{currentGroup[0].marks > 1 ? 's' : ''}
                </div>
                {isChoice && (
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--accent)', marginTop: 2 }}>
                    ⇋ Either / Or — answer ONLY ONE of the alternatives below.
                  </div>
                )}
              </div>
            </div>

            {answerLocked && (
              <div className="card" style={{
                background: 'var(--surface-2)', borderLeft: '4px solid var(--warn, #d97706)',
                marginBottom: 12, padding: 12, fontWeight: 600,
              }}>
                You've already answered the required {sectionNeed} of {sectionUnitIdx.length} question
                {sectionNeed === 1 ? '' : 's'} in this section — you can't answer more.
              </div>
            )}

            {currentGroup.map((m, idx) => (
              <div key={m.id}
                   style={isChoice ? {
                     border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 16,
                   } : undefined}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div>
                    {isChoice && (
                      <div style={{ fontWeight: 700, color: 'var(--accent)' }}>Alternative {idx === 0 ? 'A' : 'B'}</div>
                    )}
                    <div className="muted" style={{ fontSize: 12 }}>
                      {m.type === 'MultipleChoiceSingle' && 'Select one option'}
                      {m.type === 'MultipleChoiceMulti' && 'Select all that apply'}
                      {m.type === 'Descriptive' && 'Write your answer below'}
                    </div>
                  </div>
                  <SaveBadge status={s.saveStatus[m.id]} />
                </div>

                {/* Pin the question to the top of the screen while writing (multi-part questions
                    pin each part's prompt themselves). */}
                <div style={(m.parts && m.parts.length) ? { marginBottom: 16 } : {
                  position: 'sticky', top: 0, zIndex: 3,
                  background: 'var(--surface)', marginBottom: 16, paddingBottom: 8,
                }}>
                  <RichTextView html={m.prompt} style={{ fontSize: 15 }} />
                </div>

                {/* Disable the inputs when this question is locked by the section's answer quota. */}
                <fieldset disabled={answerLocked} style={{
                  border: 'none', padding: 0, margin: 0,
                  // <fieldset> sizes to its content's intrinsic width by default, so hiding the
                  // ink toolbar (wide content) would shrink the whole answer area. Force it to
                  // fill its container instead, independent of content width.
                  width: '100%', minInlineSize: 0, boxSizing: 'border-box',
                  pointerEvents: answerLocked ? 'none' : undefined,
                }}>
                  {m.type === 'Descriptive' ? (
                    <AnswerEditor
                      question={m}
                      value={s.descOf(m.id)}
                      onChange={(next) => s.setAnswer(m.id, next)}
                      sheets={{
                        mainSheet: sheetConfig.mainSheet,
                        sheetBudget,
                        questionNo: `${leadPos + 1}${isChoice ? (idx === 0 ? 'A' : 'B') : ''}`,
                      }}
                    />
                  ) : (
                    <McqEditor
                      question={m}
                      value={s.mcqOf(m.id)}
                      onChange={(next) => s.setAnswer(m.id, next)}
                      disabled={answerLocked}
                    />
                  )}
                </fieldset>
              </div>
            ))}

            <div className="row" style={{ justifyContent: 'space-between', marginTop: 20 }}>
              <button onClick={() => leadPos > 0 && setCurrentIndex(leadIndices[leadPos - 1])}
                      disabled={leadPos <= 0}>
                ← Previous
              </button>
              <button onClick={() => leadPos < totalUnits - 1 && setCurrentIndex(leadIndices[leadPos + 1])}
                      disabled={leadPos >= totalUnits - 1}>
                Next →
              </button>
            </div>
          </div>
        </div>
      </div>
      </div>
    </>
  );
}
